import {Drawer} from 'components/customer/Product/entity/Drawer';
import {CabinetTop} from 'shared/types/CabinetTop';
import {toNumber} from 'components/customer/Product/Drawer/helpers';

// Everything in mm, measured up from the outside bottom and across from the
// outside left of the carcase.

export interface InnerDrawerBox {
    index: number;
    bottom: number;
    // the runner's height; 0 until one is picked
    height: number;
    position: number;
}

export interface InnerDrawerGap {
    bottom: number;
    top: number;
}

export interface HingeVoid {
    side: 'left' | 'right';
    // the hinge side panel's inner face to the partition's outer face
    from: number;
    to: number;
    // the partition panel itself
    partitionFrom: number;
    partitionTo: number;
}

interface GeometryInput {
    height: number;
    thickness: number;
    // the panel drawn across the top
    topPart: number;
    drawers: Drawer[];
}

// What closes the cabinet's top, by its name as the layout reads it: nothing
// without a top, the rail's width on edge (0 when the top sends none, as the
// layout takes it), and a carcase thickness for any other top. An unknown top
// is drawn closed.
export const topPartOf = (top: CabinetTop, thickness: number) => {
    if (!top) return thickness;

    const name = String(top.name ?? '').toLowerCase();

    if (top.id == -1 || name === 'none' || name.includes('no top')) return 0;

    if (name.includes('rail on edge')) {
        const width = Number(top.widthFunction || 0);

        return Number.isFinite(width) ? width : 0;
    }

    return thickness;
};

// A position is from the top of the bottom panel to the underside of the box,
// for every drawer, as the layout shares the space above that panel. The 3D
// adds the panel to the bottom drawer only, so it draws the rest a panel low.
// The space ends under whatever closes the top.
export const innerDrawerGeometry = ({
    height,
    thickness,
    topPart,
    drawers,
}: GeometryInput) => {
    const floor = thickness;
    const ceiling = height - topPart;

    const boxes: InnerDrawerBox[] = drawers.map((drawer, index) => {
        const position = toNumber(drawer?.position_to_bottom);

        return {
            index,
            bottom: floor + position,
            height: Math.max(0, toNumber(drawer?.drawer_runner_height)),
            position,
        };
    });

    // bottom up, whatever order the drawers are held in
    const stacked = [...boxes].sort((a, b) => a.bottom - b.bottom);
    const gaps: InnerDrawerGap[] = [];
    let below = floor;

    stacked.forEach((box) => {
        gaps.push({bottom: below, top: box.bottom});
        below = box.bottom + box.height;
    });

    if (stacked.length) gaps.push({bottom: below, top: ceiling});

    return {floor, ceiling, boxes, gaps};
};

interface DragBounds {
    floor: number;
    ceiling: number;
    // the lowest position the box's own system allows
    lowest: number;
}

// The positions a box can be dragged between: inside the cabinet and out of
// every other box. Boxes may touch; the clearances between them are
// validation's, as for a typed position.
export const dragLimits = (
    boxes: InnerDrawerBox[],
    index: number,
    {floor, ceiling, lowest}: DragBounds
) => {
    const box = boxes.find((candidate) => candidate.index === index);

    if (!box) return {min: lowest, max: Infinity};

    let min = lowest;
    let max = ceiling - floor - box.height;

    boxes.forEach((other) => {
        if (other.index === box.index) return;

        // level boxes stack in drawer order, as drawer 1 is the top one
        const isBelow =
            other.position < box.position ||
            (other.position === box.position && other.index > box.index);

        if (isBelow) {
            min = Math.max(min, other.position + other.height);
        } else {
            max = Math.min(max, other.position - box.height);
        }
    });

    return {min, max};
};

// the partition the product puts on each hinged side, so the hinges cannot
// clash with a drawer; offset is to its inner face from outside the cabinet
export const hingeVoids = (
    width: number,
    thickness: number,
    offset: number,
    doorHang: string
): HingeVoid[] => {
    if (!(offset > thickness)) return [];

    const hang = String(doorHang ?? '').toLowerCase();
    const voids: HingeVoid[] = [];

    if (hang === 'pair' || hang === 'left') {
        voids.push({
            side: 'left',
            from: thickness,
            to: offset - thickness,
            partitionFrom: offset - thickness,
            partitionTo: offset,
        });
    }

    if (hang === 'pair' || hang === 'right') {
        voids.push({
            side: 'right',
            from: width - offset + thickness,
            to: width - thickness,
            partitionFrom: width - offset,
            partitionTo: width - offset + thickness,
        });
    }

    return voids;
};

interface Spacing {
    floor: number;
    ceiling: number;
    boxes: InnerDrawerBox[];
}

// the gaps either side of one drawer, as the drawing labels them
export const spacingAround = (
    {floor, ceiling, boxes}: Spacing,
    index: number
) => {
    const stacked = [...boxes].sort((a, b) => a.bottom - b.bottom);
    const at = stacked.findIndex((box) => box.index === index);

    if (at < 0) return null;

    const box = stacked[Number(at)];
    const below = stacked[at - 1];
    const above = stacked[at + 1];

    return {
        below: box.bottom - (below ? below.bottom + below.height : floor),
        above: (above ? above.bottom : ceiling) - (box.bottom + box.height),
        height: box.height,
    };
};
